import { useMemo } from 'react';
import { useParams } from 'react-router';
import { Trans } from '@lingui/react/macro';

import { GenericError } from '@/components/GenericError/GenericError';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import {
  ConditionFormFromGet,
  useConditionForm,
  useCreateConditionFormQuestion,
} from '@/models/conditionForm';
import { ErrorState } from '@/routes/SettingsBeta/components/ErrorState';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';
import { NotFoundError } from '@/utils/error/NotFoundError';

import { ConditionQuestion } from './ConditionQuestion';

import './ConditionForm.css';

export const ConditionForm = () => {
  const { id } = useParams();

  const {
    data: conditionForm,
    isLoading,
    isError,
    error,
  } = useConditionForm(id, { enabled: !!id });

  const sortedQuestions = useMemo(
    () =>
      conditionForm
        ? [...conditionForm.questions].sort(
            (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
          )
        : [],
    [conditionForm]
  );

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !conditionForm) {
    let errorMessage = <GenericError />;

    if (error instanceof NotFoundError) {
      errorMessage = (
        <Paragraph>
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.not-found">
            This condition form could not be found or you do not have access to it.
          </Trans>
        </Paragraph>
      );
    }

    return <ErrorState errorMessage={errorMessage} />;
  }

  return (
    <main>
      <Stack
        row
        className="settings-section__record-header"
        gap="0.5rem"
        alignItems="center"
        flexWrap="wrap"
      >
        <SettingsBreadcrumbParent
          to="/settings_beta/workflows/condition-forms"
          label={
            <Trans id="settings.organization.data-catalogs.condition-forms">Condition forms</Trans>
          }
        />
        <Title as="h1" size="200" weight="medium">
          <ConditionFormPageTitle type={conditionForm.type} />
        </Title>
      </Stack>

      <Stack gap="1rem">
        <ConditionFormQuestionsHeader conditionFormId={conditionForm.id} />

        {sortedQuestions.map((question) => (
          <ConditionQuestion key={question.id} form={conditionForm} question={question} />
        ))}

        {sortedQuestions.length === 0 && (
          <Paragraph size="100" color="secondary">
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.questions.none">
              No questions yet.
            </Trans>
          </Paragraph>
        )}
      </Stack>
    </main>
  );
};

const ConditionFormQuestionsHeader = ({ conditionFormId }: { conditionFormId: string }) => {
  const showToast = useShowToast();
  const {
    mutateAsync: createConditionFormQuestion,
    isPending: isCreateConditionFormQuestionPending,
  } = useCreateConditionFormQuestion();

  const handleAddQuestion = async () => {
    try {
      await createConditionFormQuestion({ conditionFormId });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.questions.add.error">
            Could not add a question. Please try again.
          </Trans>
        ),
        type: 'error',
      });

      throw new Error('Add question failed');
    }
  };

  return (
    <Stack row alignItems="center" justifyContent="space-between">
      <Title as="h3" size="300" weight="medium">
        <Trans id="settings.organization.data-catalogs.condition-forms.detail.questions">
          Questions
        </Trans>
      </Title>

      <Button
        variant="link-subtle"
        size="small"
        isLoading={isCreateConditionFormQuestionPending}
        onPress={handleAddQuestion}
      >
        <IconAdd />
        <Trans id="settings.organization.data-catalogs.condition-forms.detail.questions.add">
          Add question
        </Trans>
      </Button>
    </Stack>
  );
};

const ConditionFormPageTitle = ({ type }: { type: ConditionFormFromGet['type'] }) => {
  switch (type) {
    case 'initial_condition':
      return (
        <Trans id="settings.organization.data-catalogs.condition-forms.detail.title.initial-condition">
          Initial condition form
        </Trans>
      );
    case 'quality_control':
      return (
        <Trans id="settings.organization.data-catalogs.condition-forms.detail.title.quality-control">
          Quality control form
        </Trans>
      );
  }
};
